import "config/config"; // This import has to happen before an other import accesses NextJS config
import { configureSentryScope, initSentry } from "logger/sentry-config";
import { useEffect } from "react";
import type {
    AppContext as NextAppContext,
    AppProps as NextAppProps
} from "next/app";
import Head from "next/head";
import Script from "next/script";
import Router from "next/router";
import parseUrl from "parseurl";
import getConfig from "next/config";
import { ThemeProvider } from "styled-components";
import Backbone from "pages/common/lib/backbone-helpers";
import { randomBytes } from "crypto";

import { api, GrabyoApi } from "api/base";
import type { LoggedInUser } from "api/User.types";
import type { Organization } from "api/organizations.types";
import { getUnleashFlags, initUnleash } from "api/unleash";
import { getIntercomSecret } from "util/secret-utils";
import { hasRole } from "util/role-utils";
import {
    environmentType,
    getFavicon,
    MASTER_ORGANIZATION_ID
} from "util/env-utils";
import type { Announcement } from "util/dom-utils";
import DOMUtils from "util/dom-utils";
import {
    publicPaths,
    registerServiceWorker,
    setupJqueryAuthPrefilter,
    unregisterServiceWorker
} from "util/auth-utils";
import {
    applyCSP,
    CSPSetting,
    LIST_OF_BASE_URLS_THAT_SHOULD_HAVE_THE_CSP_ENABLED
} from "util/security-utils";
import { AppContext } from "util/react-utils";

import Constants from "const/constants";
import { pages } from "const/pages";

import Theme from "style/theme";
import "react-tippy/dist/tippy.css";
import "@grabyo/icon-font/style.css";
import "css/grabyo-custom-bootstrap-daterangepicker-bs3.css";
import "css/studio-style.css";
import "css/jquery-mentionsInput-grabyo.css";
import Error from "./_error";

import {
    createAppShell,
    initCredentialRefreshTimer,
    initStores,
    initIntercom,
    initAnalytics,
    buildLoginUrl
} from "pages/common/state/utils";
import type {
    FeaturesConfigInterface,
    StoreInterface
} from "pages/common/types/page";
import type { NextComponentType, NextPageContext } from "next";
import { isMonitoringLink } from "pages/producer/monitoring-link/utils";
import {
    isProducerGatewayPath,
    producerGatewayLoginPath
} from "pages/producer/gateway/utils/path.utils";
import { BackboneStateContext } from "pages/common/hooks/useBackboneState";
import type { GrabyoJWTPayload } from "api/GrabyoJWTPayload";

export type EnableChecker = (
    featuresConfig: FeaturesConfigInterface,
    isAdmin?: boolean,
    isMasterOrganization?: boolean
) => boolean;

export interface BaseWrappedPageProps extends PageProps {
    appStore: StoreInterface;
    app: AppInterface;
}

//all pages have these
export interface PageProps {
    featuresConfig: FeaturesConfigInterface;

    isAdmin?: boolean;
    loggedInUser?: LoggedInUser;
    jwtPayload?: GrabyoJWTPayload;
    tokenAgeSeconds: number;

    nonce: string;
    hostname: string;
    pageName: string;
    path: string;
    isPathPublic: boolean;
    queryParameters: Record<string, string | string[]>; //ParsedUrlQuery

    announcements?: Announcement[];

    organization?: Organization;
    organizationId?: string;
    isMasterOrganization?: boolean;

    intercomHash?: string;
    enableIntercom?: boolean;

    localStorageVersion: number;
    displayStreamSettingsButton: boolean;
    displayStreamShareButton: boolean;
    environment: string;
    version: string;

    statusCode: number;

    //guessing this is a part of platform config
    producerMaxGuests: number;
    platformDefaultInlineTypeConfig: string; //JSON as string
    maxConcurrentBroadcastDestinationsPerStream: string;
}

export type WrappedPage<P = Record<string, unknown>> = NextComponentType<
    NextPageContext,
    unknown,
    BaseWrappedPageProps & P
> & { isEnabled?: EnableChecker };

type AppProps<P> = {
    pageProps: P;
    Component: WrappedPage<P>;
} & Omit<NextAppProps<P>, `pageProps` | `Component`>;

export const GrabApp = ({ Component, pageProps }: AppProps<PageProps>) => {
    const { hostname, path, organization, loggedInUser, jwtPayload } =
        pageProps;

    initSentry(hostname, path, jwtPayload);

    //create App global singleton and some backbones models
    const globalApp = createAppShell(pageProps);

    //leak app to window for utility purposes
    if (typeof window !== `undefined`) {
        window.App = globalApp;
    } else {
        (global as ExtendedGlobal).App = globalApp;
    }

    //create proper store structures inside App singleton and populate with organization and user
    initStores(organization, loggedInUser, pageProps, globalApp);

    //init intercom
    initIntercom(pageProps);

    useEffect(() => {
        try {
            if (!Backbone.history.start()) {
                // We arrive here if no initial route if matched
                location.hash = ``;
            }
        } catch (err) {}

        // Disable NextJS client-side routing as it interferes with Backbone.Router
        Router.beforePopState(() => false);

        const {
            isPathPublic,
            pageName,
            announcements,
            loggedInUser,
            tokenAgeSeconds,
            featuresConfig
        } = pageProps;

        //heap + GA
        initAnalytics(pageProps);

        initCredentialRefreshTimer(loggedInUser, tokenAgeSeconds, api);

        if (featuresConfig?.studioFeature?.unleashServiceWorker) {
            if (!isPathPublic) {
                registerServiceWorker();
            }
        } else {
            unregisterServiceWorker();
        }

        $.ajaxSetup({
            timeout: 300000,
            dataType: `json`,
            cache: false,
            beforeSend: (_jqXHR, options) => {
                const { contentType, data } = options;
                if (
                    contentType === `application/json` &&
                    typeof data !== `string`
                ) {
                    options.data = JSON.stringify(data);
                }
            },
            statusCode: {
                // In studiotimeline.jsp App.Models.user doesn't exist
                401: () => {
                    if (!isPathPublic) {
                        DOMUtils.handleUnauthorisedRequest();
                    }
                }
            }
        });

        if (!isPathPublic) {
            setupJqueryAuthPrefilter();
            api.addTokenExpiryInterceptor();
        }

        DOMUtils.preventDragAndDrop();
        DOMUtils.displayAnnouncements(announcements, pageName);
    }, []);

    const { publicRuntimeConfig } = getConfig();
    const { HEAP_API_KEY } = publicRuntimeConfig;

    return pageProps.statusCode >= 400 ? (
        <Error statusCode={pageProps.statusCode} />
    ) : (
        <AppContext.Provider
            value={{
                ...pageProps,
                appStore: globalApp.Store,
                homeUIStore: globalApp.HomeUIStore
            }}
        >
            <BackboneStateContext.Provider
                value={{
                    Models: App.Models,
                    Collections: App.Collections,
                    Routers: App.Routers
                }}
            >
                <Head>
                    <meta
                        name="viewport"
                        content="width=device-width, initial-scale=1, maximum-scale=1"
                    />
                    <link
                        rel="icon"
                        type="image/png"
                        href={getFavicon(pageProps.hostname)}
                    />
                    <link
                        rel="stylesheet"
                        href="https://dwo3ckksxlb0v.cloudfront.net/static/css/bootstrap/3.3.7/bootstrap.min.css?sri"
                        integrity="sha384-7uRFHViUWm0FBCyo536V5d0xL2m/QXjkPbYBZwo1mTLx1eAxxXVIaGME3yAqq49Y"
                        crossOrigin="anonymous"
                    />
                </Head>
                {/* NOTE! We had to remove `strategy="beforeinteractive" to get the inline script to work with NextJS 12.1.0 `*/}
                <Script
                    data-testid="heap-analytics-loader"
                    nonce={pageProps.nonce}
                >
                    {`
                        window.heapReadyCb=window.heapReadyCb||[],window.heap=window.heap||[],heap.load=function(e,t){window.heap.envId=e,window.heap.clientConfig=t=t||{},window.heap.clientConfig.shouldFetchServerConfig=!1;var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="https://cdn.us.heap-api.com/config/"+e+"/heap_config.js";var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(a,r);var n=["init","startTracking","stopTracking","track","resetIdentity","identify","identifyHashed","getSessionId","getUserId","getIdentity","addUserProperties","addEventProperties","removeEventProperty","clearEventProperties","addAccountProperties","addAdapter","addTransformer","addTransformerFn","onReady","addPageviewProperties","removePageviewProperty","clearPageviewProperties","trackPageview"],i=function(e){return function(){var t=Array.prototype.slice.call(arguments,0);window.heapReadyCb.push({name:e,fn:function(){heap[e]&&heap[e].apply(heap,t)}})}};for(var p=0;p<n.length;p++)heap[n[p]]=i(n[p])};
                        heap.load("${HEAP_API_KEY}", {
                            enableSecureCookies: true
                        });
                    `}
                </Script>
                {/* https://nextjs.org/docs/messages/next-script-for-ga#using-gtagjs-recommended*/}
                <Script
                    src="https://www.googletagmanager.com/gtag/js?id=G-CW43610MTX"
                    strategy="lazyOnload"
                />
                <Script id="google-analytics" strategy="beforeInteractive">
                    {`
                          window.dataLayer = window.dataLayer || [];
                          function gtag(){dataLayer.push(arguments);}
                          gtag('js', new Date());

                          gtag('config', 'G-CW43610MTX');
                        `}
                </Script>
                <ThemeProvider theme={Theme}>
                    <Component
                        app={globalApp}
                        appStore={globalApp.Store}
                        {...pageProps}
                    />
                </ThemeProvider>
            </BackboneStateContext.Provider>
        </AppContext.Provider>
    );
};

//TODO: not sure we can use getServerSideProps here (nextjs 12)
GrabApp.getInitialProps = async ({
    ctx,
    router,
    Component
}: Omit<NextAppContext, `Component`> & {
    Component: WrappedPage;
}): Promise<{ pageProps: PageProps }> => {
    const { req, res, pathname } = ctx;
    const api = new GrabyoApi(ctx);
    const hostname = api.hostname;

    const page = pages.find(({ href }) => href === router.pathname);
    const pageName = (page ? page.legacyHref : router.pathname).slice(1); // strip leading '/'

    await initUnleash();

    const unleashContext = { hostname };
    const isPathPublic =
        publicPaths.includes(pathname) || isMonitoringLink(pathname);
    const nonce: string = randomBytes(16).toString("base64");

    /**
     * Store the equivilant of the Express.Request.baseUrl,
     * which will be used to determine if the CSP is enabled
     * on that page.
     *
     * Currently only the `/login` base URL has the CSP enabled.
     */

    /**
     * Check through the list and determine if the CSP should
     * be applied to the HTTP response for this page.
     */
    if (LIST_OF_BASE_URLS_THAT_SHOULD_HAVE_THE_CSP_ENABLED.includes(pathname)) {
        /**
         * Notes on applying the CSP.
         *
         * We currently have an Engineering goal for Q3 2022.
         * Which is to enable the CSP on the login page.
         *
         * For the `login` page it should be enabled,
         * and not configured to only report violations.
         *
         * Over time, more pages will also have a CSP enabled.
         * So this code is likely to be refactored over time.
         *
         * @see {@link https://grabyo.atlassian.net/browse/PLAT-1817|Jira}
         */
        applyCSP(res, nonce, CSPSetting.Experimental);
    } else {
        const { unleashContentSecurityPolicy } =
            getUnleashFlags(unleashContext);
        /**
         * If the feature flag is set to `true`,
         * apply the `report-only` CSP to the HTTP response.
         */
        const cspSetting = unleashContentSecurityPolicy
            ? CSPSetting.Test
            : undefined;

        applyCSP(res, nonce, cspSetting);
    }

    let tokenAge: number;
    if (!isPathPublic) {
        try {
            const { jwtTokenAgeSeconds } = await api.refreshToken();
            tokenAge = jwtTokenAgeSeconds;
        } catch ({ response: { status, data } }) {
            if (status < 400 || status >= 500) {
                console.error(data);
            }

            const { search } = parseUrl(req);
            const encodedSearch = search ? encodeURIComponent(search) : ``;
            const redirectPath = `${pageName}${encodedSearch}`;
            const location = isProducerGatewayPath(pathname)
                ? producerGatewayLoginPath
                : buildLoginUrl(hostname, redirectPath);

            res.writeHead(302, {
                Location: location
            });
            res.end();
            return;
        }
    }

    //we assume public page props
    const props: PageProps = {
        featuresConfig: {
            studioFeature: getUnleashFlags(unleashContext)
        },
        isAdmin: false,
        intercomHash: ``,
        queryParameters: router.query,
        localStorageVersion: 13,
        displayStreamSettingsButton: true,
        displayStreamShareButton: true,
        environment: environmentType,
        version: environmentType,
        path: pathname,
        pageName,
        tokenAgeSeconds: tokenAge,
        hostname,
        isPathPublic,
        nonce,
        statusCode: res.statusCode,
        producerMaxGuests: 4, //TODO: this should be a const somewhere, not defaulted to here
        platformDefaultInlineTypeConfig: "",
        maxConcurrentBroadcastDestinationsPerStream: "",
        jwtPayload: api.jwtPayload
    };

    props.enableIntercom = !(
        isProducerGatewayPath(pathname) || isMonitoringLink(pathname)
    );

    //we override
    if (!isPathPublic) {
        initSentry(req.headers.host, router.pathname, api.jwtPayload);

        let organization;
        let loggedInUser;
        let platformConfig;
        let intercomHash = "";

        const getOrganization = async () => {
            try {
                organization = await api.getOrganization();
            } catch (err) {
                console.warn(`[LOAD-ERROR] Failed to load organization`);
                organization = { data: {} };
            }
        };

        const getLoggedInUserAndIntercomSecret = async () => {
            try {
                loggedInUser = await api.getLoggedInUser();
            } catch (err) {
                console.warn(`[LOAD-ERROR] Failed to load logged in user`);
                loggedInUser = {};
            }
            try {
                intercomHash = await getIntercomSecret(loggedInUser.email);
            } catch (e) {
                console.warn(`[LOAD-ERROR] Failed to get Intercom secret`);
            }
        };

        const getPlatformConfig = async () => {
            try {
                platformConfig = await api.getPlatformConfig();
            } catch (err) {
                console.warn(
                    `[LOAD-ERROR] Failed to load platform config`,
                    err
                );
                platformConfig = {};
            }
        };

        await Promise.all([
            getOrganization(),
            getLoggedInUserAndIntercomSecret(),
            getPlatformConfig()
        ]);

        if (organization.data.featuresConfig) {
            organization.data.featuresConfig.studioFeature = {
                ...organization.data.featuresConfig.studioFeature,
                ...getUnleashFlags({
                    ...unleashContext,
                    organizationId: organization.data.organizationId,
                    userId: loggedInUser.userId
                })
            };
        }
        organization.data.defaultResolution = platformConfig.defaultResolution;
        platformConfig.announcements = JSON.parse(platformConfig.announcements);

        Object.assign(props, {
            loggedInUser,
            isAdmin: hasRole(loggedInUser, Constants.USER_ROLES.ADMIN),
            intercomHash,
            organization: organization.data,
            organizationId: organization.data.organizationId,
            isMasterOrganization:
                organization.data.organizationId === MASTER_ORGANIZATION_ID,
            featuresConfig: organization.data.featuresConfig || {},
            ...platformConfig
        });
    }

    //we don't override other problems
    if (props.statusCode === 200) {
        //we check if the page component is accessible by the user, default is true because some pages may not have this check
        let enabled = true;
        if (Component.isEnabled) {
            enabled = Component.isEnabled(
                props.featuresConfig,
                props.isAdmin,
                props.isMasterOrganization
            );
        }

        if (!enabled) {
            props.statusCode = 403;
        }
    }

    return { pageProps: props };
};

export default GrabApp;
